<template>
  <div class="calculator operating-calculator" v-loading="loading">
    <div class="CARD_tit">经营数据对应业绩计算器</div>
    <div class="downloadBtns">
      <CButtonsDownload :dButtonsDownloads="downloadButtons" @fnButtondownload="download" />
      <download-excel ref="excel" style="display:none" :data="exportRows" :fields="exportFields"
        name="EBITDA 及可供分配金额情况预测.xls" />
    </div>
    <div class="calculator-body">
      <div class="chart-pane">
        <el-button class="expand-chart" icon="el-icon-full-screen" aria-label="放大预测图" @click="dialogVisible = true" />
        <div ref="chart" class="forecast-chart"></div>
      </div>
      <div class="calculator_content">
        <CAssetNavigator v-if="names.length" v-model="selected" :options="names" />
        <div v-if="activeAsset" class="parameters">
          <p v-if="capacity !== null" class="capacity">功率容量：{{ capacity.toLocaleString() }} kW</p>
          <p v-if="activeAsset.included_in_base_period === false" class="notice">本项目尚无基期经营数据，未纳入本期测算。当前已披露项目容量与整体容量一致。</p>
          <div class="row column-labels"><span></span><span>取值</span><span>上限（90分位）</span><span>下限（10分位）</span></div>
          <div v-for="field in fields" :key="field.key" class="row">
            <label>{{ field.label }}</label>
            <el-input v-for="point in points" :key="point" :value="activeAsset[field.key][point]"
              :label="field.label + ' ' + point" placeholder="待填写"
              @input="$set(activeAsset[field.key], point, $event)" />
          </div>
          <div v-for="field in marginFields" :key="field.key" class="row">
            <label>{{ field.label }}</label>
            <el-input v-for="point in points" :key="point" v-model="config[field.key][point]"
              :label="field.label + ' ' + point" placeholder="待填写" />
          </div>
        </div>
        <p v-else-if="loaded" class="notice">暂无可用的资产经营参数</p>
        <p class="formula">{{ formula }}</p>
        <p v-if="loaded && !hasData" class="notice">暂无可用于预测的季度财报，已有经营指标仍可查看。</p>
        <p v-if="missing.length" class="notice">以下参数暂无依据，请填写测算假设：{{ missing.join('、') }}。缺失值不按零计算。</p>
        <p v-if="error" class="notice" role="alert">{{ error }}</p>
        <el-button class="calculate-button" type="primary" :disabled="loading" @click="calculate(true)">计算</el-button>
      </div>
    </div>
    <CDialog :id="'operating-calculator-' + _uid" :DIALOGHEIGHT="heightDialog" :dialogVisible="dialogVisible">
      <el-dialog title="EBITDA 及可供分配金额情况预测" :visible.sync="dialogVisible" :width="widthDialog" :top="topDialog"
        @opened="renderDialog" @closed="disposeDialog">
        <el-dropdown v-if="popupLayouts.length" trigger="click" @command="changeLayout">
          <span>{{ popupLayouts[popupIndex] }}<i class="el-icon-arrow-down"></i></span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="(label, index) in popupLayouts" :key="index" :command="index">{{ label }}</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <div ref="dialogChart" :style="{ height: heightDialog }"></div>
      </el-dialog>
    </CDialog>
  </div>
</template>

<script>
import CAssetNavigator from '@/components/Basic/CAssetNavigator';
import CButtonsDownload from '@/components/Basic/CButtons_download';
import CDialog from '@/components/Basic/CDialog';
import html2canvas from 'html2canvas';
import { fnDownloadBgc } from '@/utils/util';

const clone = value => JSON.parse(JSON.stringify(value));
const numeric = value => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const number = Number(String(value).replace(/,/g, '').replace(/%/g, ''));
  return Number.isFinite(number) ? number : null;
};
const metrics = [
  { key: 'ebitda_ttm', label: 'EBITDA ttm走势及预测', color: '#4CD5D8' },
  { key: 'distributable_amount(period)_ttm', label: '可供分配金额走势及预测', color: '#7770E8' },
  { key: 'div_yield(annulized)', label: '对应分派率_ttm走势及预测', color: '#AF89F5' },
];

export default {
  name: 'OperatingCalculator',
  components: { CAssetNavigator, CButtonsDownload, CDialog },
  props: { code: { type: String, default: '' } },
  data() {
    return {
      loading: false, loaded: false, requestNumber: 0, error: '', selected: '', fields: [], formula: '',
      missing: [], hasData: false, history: [], forecasts: [], source: {},
      config: { asset_config: [], ebitda_margin_list: {}, div_margin_list: {} },
      points: ['mean', 'max', 'min'],
      marginFields: [{ key: 'ebitda_margin_list', label: 'EBITDA Margin' }, { key: 'div_margin_list', label: 'Div Margin' }],
      downloadButtons: [{ id: '241072', name: '下载图片' }, { id: '241073', name: '下载表格' }],
      dialogVisible: false, widthDialog: '85%', topDialog: '5vh', heightDialog: '65vh', popupIndex: 0,
    };
  },
  computed: {
    names() { return this.config.asset_config.map(asset => asset.name); },
    activeAsset() { return this.config.asset_config.find(asset => asset.name === this.selected); },
    capacity() { return this.activeAsset ? numeric(this.activeAsset.gfa) : null; },
    effectiveCode() { return this.code || window.sessionStorage.getItem('CURRENTCODE'); },
    theme() { return this.$store.state.theme; },
    popupLayouts() { return this.$store.state.popUpWindowDatas || []; },
    exportFields() {
      const fields = { '报告期': 'rp_period' };
      metrics.forEach(metric => {
        fields[metric.label] = metric.key;
        fields[metric.label + '上限'] = metric.key + '_max';
        fields[metric.label + '下限'] = metric.key + '_min';
      });
      return fields;
    },
    exportRows() {
      const rows = {};
      [...this.history, ...this.forecasts].forEach(item => {
        if (!rows[item.rp_period]) rows[item.rp_period] = { rp_period: item.rp_period };
        const row = rows[item.rp_period];
        row[item.key] = Object.prototype.hasOwnProperty.call(item, 'value') ? item.value : item.mean;
        if (Object.prototype.hasOwnProperty.call(item, 'max')) {
          row[item.key + '_max'] = item.max;
          row[item.key + '_min'] = item.min;
        }
      });
      return Object.values(rows).sort((a, b) => a.rp_period.localeCompare(b.rp_period));
    },
  },
  mounted() { this.calculate(false); window.addEventListener('resize', this.resize); },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize);
    if (this.chart) this.chart.dispose();
    this.disposeDialog();
  },
  watch: {
    theme() { this.render(); if (this.dialogVisible) this.renderDialog(); },
    code() {
      this.loaded = false; this.selected = ''; this.history = []; this.forecasts = [];
      this.config = { asset_config: [], ebitda_margin_list: {}, div_margin_list: {} };
      this.calculate(false, true);
    },
  },
  methods: {
    formatConfig(config) {
      const result = clone(config);
      const format = (values, percent) => this.points.forEach(point => {
        const value = numeric(values[point]);
        values[point] = value === null ? '' : percent ? (value * 100).toFixed(2) + '%' : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
      });
      result.asset_config.forEach(asset => this.fields.forEach(field => format(asset[field.key], field.percent)));
      this.marginFields.forEach(field => format(result[field.key], true));
      return result;
    },
    requestConfig() {
      const result = clone(this.config);
      const parse = (values, percent) => this.points.forEach(point => {
        const value = numeric(values[point]);
        if (value === null && String(values[point] || '').trim() !== '') throw Error('请输入有效数字');
        values[point] = value === null ? null : percent ? value / 100 : value;
      });
      result.asset_config.forEach(asset => this.fields.forEach(field => {
        parse(asset[field.key], field.percent);
        this.points.forEach(point => {
          const value = asset[field.key][point];
          if (value === null) return;
          if (field.key === 'avg_occ' && (value < 0 || value > 1)) throw Error('平均上架率应在 0% 至 100% 之间');
          if (['comeff_rent_yoy', 'income_growth'].includes(field.key) && value < -1) throw Error('年化增长率不能低于 -100%');
          if (!field.percent && value < 0) throw Error('收入和服务单价不能为负数');
        });
      }));
      this.marginFields.forEach(field => parse(result[field.key], true));
      return result;
    },
    async calculate(edit, reload = false) {
      if ((this.loading && !reload) || !this.effectiveCode) return;
      const requestNumber = ++this.requestNumber;
      const code = this.effectiveCode;
      this.loading = true; this.error = '';
      try {
        const body = edit && this.loaded ? new URLSearchParams({ config_data: JSON.stringify(this.requestConfig()) }).toString() : '';
        const { data } = await this.$https.post('/api/v4/get_fin_data_quarterly_operation?code=' + encodeURIComponent(code), body,
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
        if (requestNumber !== this.requestNumber || code !== this.effectiveCode) return;
        if (data.code !== 200) throw Error(data.msg || '计算失败');
        const result = data.data;
        this.fields = result.fields; this.formula = result.formula;
        this.config = this.formatConfig(result.default_config);
        if (!this.names.includes(this.selected)) this.selected = this.names[0] || '';
        this.history = result.list; this.forecasts = result.yugu_list;
        this.source = result.source; this.hasData = result.has_data;
        this.missing = result.missing_parameters || []; this.loaded = true;
        await this.$nextTick(); this.render();
        if (this.dialogVisible) this.renderDialog();
      } catch (error) {
        if (requestNumber !== this.requestNumber) return;
        this.error = error.message || '计算器加载失败，请点击计算重试';
        this.$message.error(this.error);
      } finally { if (requestNumber === this.requestNumber) this.loading = false; }
    },
    chartOption() {
      const rows = this.exportRows;
      const periods = rows.map(row => row.rp_period);
      const forecastPeriods = new Set(this.forecasts.map(row => row.rp_period));
      const firstForecast = periods.findIndex(period => forecastPeriods.has(period));
      const amount = (value, metric) => {
        const number = numeric(value);
        return number === null ? null : metric.key === 'div_yield(annulized)' ? number * 100 : number / 10000;
      };
      const series = [];
      metrics.forEach((metric, index) => {
        const common = { name: metric.label, type: index === 2 ? 'bar' : 'line', yAxisIndex: index === 2 ? 1 : 0,
          itemStyle: { color: metric.color }, connectNulls: false };
        series.push({ ...common, data: rows.map((row, i) => firstForecast >= 0 && i >= firstForecast ? null : amount(row[metric.key], metric)) });
        series.push({ ...common, lineStyle: { type: 'dashed', color: metric.color },
          data: rows.map((row, i) => firstForecast >= 0 && i >= firstForecast - (index === 2 ? 0 : 1) ? amount(row[metric.key], metric) : null) });
        if (index < 2) {
          const bounds = rows.map(row => [amount(row[metric.key + '_min'], metric), amount(row[metric.key + '_max'], metric)]);
          if (firstForecast > 0) {
            const anchor = amount(rows[firstForecast - 1][metric.key], metric);
            bounds[firstForecast - 1] = [anchor, anchor];
          }
          series.push({ name: metric.label + '区间', type: 'custom', silent: true,
            encode: { x: 0, y: [1, 2] }, tooltip: { show: false },
            data: bounds.map(([min, max], i) => [i, min, max]),
            renderItem: (params, api) => {
              const i = api.value(0), current = bounds[i], next = bounds[i + 1];
              if (!next || [...current, ...next].some(value => value === null)) return null;
              const points = [api.coord([i, current[0]]), api.coord([i, current[1]]),
                api.coord([i + 1, next[1]]), api.coord([i + 1, next[0]])];
              return { type: 'polygon', shape: { points }, style: { fill: metric.color, opacity: .18 } };
            },
          });
        }
      });
      const color = this.theme === 'dark' ? '#ddd' : '#555';
      return {
        title: { text: 'EBITDA 及可供分配金额情况预测', left: 'center', textStyle: { color, fontSize: 14 } },
        legend: { data: metrics.map(m => m.label), top: 32, textStyle: { color, fontSize: 11 } },
        toolbox: { right: 15, top: 58, feature: { dataZoom: { yAxisIndex: 'none' }, restore: {} } },
        grid: { left: 65, right: 55, top: 95, bottom: 65 },
        tooltip: { trigger: 'axis', formatter: items => {
          if (!items.length) return '';
          const row = rows.find(item => item.rp_period === items[0].name);
          if (!row) return '';
          const format = (value, metric) => {
            const number = amount(value, metric);
            return number === null ? '--' : number.toLocaleString(undefined, { maximumFractionDigits: 2 }) + (metric.key === 'div_yield(annulized)' ? '%' : '万元');
          };
          return row.rp_period + metrics.map(metric => '<br>' + metric.label + '：' + format(row[metric.key], metric)
            + (forecastPeriods.has(row.rp_period) ? '<br>区间：' + format(row[metric.key + '_min'], metric) + ' ～ ' + format(row[metric.key + '_max'], metric) : '')).join('');
        } },
        xAxis: { type: 'category', data: periods, axisLabel: { color, rotate: 35 } },
        yAxis: [{ type: 'value', name: '单位：万元', axisLabel: { color }, nameTextStyle: { color },
          splitLine: { lineStyle: { color: this.theme === 'dark' ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)', type: 'dashed' } } },
          { type: 'value', name: '分派率（%）', axisLabel: { color, formatter: '{value}%' }, nameTextStyle: { color }, splitLine: { show: false } }],
        series,
      };
    },
    render() {
      if (!this.$refs.chart) return;
      if (!this.chart) this.chart = this.$echarts.init(this.$refs.chart);
      this.chart.setOption(this.chartOption(), true);
    },
    renderDialog() {
      if (!this.$refs.dialogChart) return;
      if (!this.dialogChart) this.dialogChart = this.$echarts.init(this.$refs.dialogChart);
      this.dialogChart.setOption(this.chartOption(), true);
    },
    changeLayout(index) {
      const config = this.$store.state.popUpWindowConfi[index];
      this.popupIndex = index; this.widthDialog = config.width_dialog;
      this.topDialog = config.top_dialog; this.heightDialog = config.height_chart;
      this.$nextTick(this.resize);
    },
    disposeDialog() { if (this.dialogChart) this.dialogChart.dispose(); this.dialogChart = null; },
    resize() { if (this.chart) this.chart.resize(); if (this.dialogChart) this.dialogChart.resize(); },
    async download(name) {
      if (name === '下载表格') { this.$refs.excel.$el.click(); return; }
      const canvas = await html2canvas(this.$refs.chart, { backgroundColor: fnDownloadBgc(this.theme === 'dark') });
      const link = document.createElement('a'); link.href = canvas.toDataURL('image/png');
      link.download = 'EBITDA 及可供分配金额情况预测.png'; link.click();
    },
  },
};
</script>

<style scoped lang="less">
.operating-calculator { position: relative; padding: 20px; color: var(--table_color); }
.CARD_tit { border-left: 4px solid #4CD5D8; padding-left: 14px; font-size: 18px; font-weight: 600; }
.downloadBtns { display: flex; justify-content: flex-end; margin: 12px 0; }
.calculator-body { display: flex; gap: 28px; align-items: flex-start; }
.chart-pane { position: relative; width: 58%; min-width: 0; }
.forecast-chart { height: 460px; width: 100%; }
.expand-chart { position: absolute; top: -15px; right: 0; z-index: 1; padding: 6px; background: transparent; border: none; color: inherit; }
.calculator_content { flex: 1; min-width: 0; }
.row { display: grid; grid-template-columns: 1.7fr repeat(3, 1fr); gap: 8px; align-items: center; margin: 12px 0; font-size: 13px; }
.column-labels { text-align: center; font-size: 12px; }
.capacity, .formula, .notice { font-size: 12px; line-height: 1.7; margin: 10px 0; }
.notice { color: var(--stock_area_item_color1); }
.calculate-button { float: right; margin-top: 10px; min-width: 120px; }
/deep/ .el-input__inner { background: transparent; color: var(--table_color); text-align: center; padding: 0 5px; font-size: 12px; }
@media (max-width: 1050px) { .calculator-body { flex-direction: column; } .chart-pane, .calculator_content { width: 100%; } }
</style>
